<template>
  <div class="order-detail-page">
    <div class="container" v-loading="loading">
      <div class="header">
        <h2 class="title">订单详情</h2>
        <el-button @click="goBack">返回</el-button>
      </div>

      <template v-if="order">
        <el-descriptions :column="2" border class="desc">
          <el-descriptions-item label="订单编号">{{ order.orderCode || "—" }}</el-descriptions-item>
          <el-descriptions-item label="国家平台单号">
            <span v-if="order.ncnpOrderId">
              {{ order.ncnpOrderId }}
              <i
                class="el-icon-copy-document"
                style="cursor: pointer; color: #1677ff; margin-left: 6px"
                title="复制"
                @click="copyText(order.ncnpOrderId)"
              ></i>
            </span>
            <span v-else>—</span>
          </el-descriptions-item>
          <el-descriptions-item label="外部平台单号">{{ order.externalOrderId || "—" }}</el-descriptions-item>
          <el-descriptions-item label="算力配置">{{ order.goodsName || "—" }}</el-descriptions-item>
          <el-descriptions-item label="算力类型">{{ goodsTypeText }}</el-descriptions-item>
          <el-descriptions-item label="地区">{{ regionText }}</el-descriptions-item>
          <el-descriptions-item label="数量">{{ order.quantity != null ? order.quantity : "—" }}</el-descriptions-item>
          <el-descriptions-item label="时长">{{ durationText }}</el-descriptions-item>
          <el-descriptions-item label="订单金额">¥{{ order.orderAmount != null ? order.orderAmount : "—" }}</el-descriptions-item>
          <el-descriptions-item label="支付状态">
            <el-tag size="mini" :type="payTagType">{{ paymentText }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="退款状态">{{ refundText }}</el-descriptions-item>
          <el-descriptions-item label="同步状态">{{ syncText }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ order.orderTime || "—" }}</el-descriptions-item>
          <el-descriptions-item label="状态更新时间">{{ order.orderStatusUpdateTime || "—" }}</el-descriptions-item>
        </el-descriptions>

        <div class="actions" v-if="order.paymentStatus === 0 && (order.ncnpOrderId || order.channelOrderId)">
          <el-button type="primary" @click="goPay">去支付</el-button>
        </div>
      </template>

      <el-empty v-else-if="!loading" description="未找到订单" />
    </div>
  </div>
</template>

<script>
import { orderDetail } from "@/api/index";

const PAYMENT_TEXT = { 0: "未支付", 1: "已支付", 2: "已取消", 3: "超时关闭", 4: "支付失败" };
const REFUND_TEXT = { 0: "无退款", 1: "退款中", 2: "已退款", 3: "退款失败" };
const SYNC_TEXT = { 0: "未同步", 1: "已同步", 2: "同步失败" };
// 与后端 mapRegion / mapCategory 一致：地区 1上海/2江苏/3浙江/4安徽；类型 1智算/2超算/3通算/4量算
const REGION_LABELS = { 1: "上海", 2: "江苏", 3: "浙江", 4: "安徽" };
const CATEGORY_LABELS = { 1: "智算", 2: "超算", 3: "通算", 4: "量算" };
const UNIT_LABELS = { HOUR: "小时", MONTH: "月", YEAR: "年", PACKAGE: "包" };

export default {
  name: "OrderDetail",
  data() {
    return {
      loading: false,
      order: null,
    };
  },
  computed: {
    goodsTypeText() {
      if (!this.order || this.order.goodsType == null) return "—";
      return CATEGORY_LABELS[this.order.goodsType] || this.order.goodsType;
    },
    regionText() {
      if (!this.order || this.order.orderSource == null) return "—";
      return REGION_LABELS[this.order.orderSource] || this.order.orderSource;
    },
    durationText() {
      if (!this.order || this.order.duration == null) return "—";
      const unit = UNIT_LABELS[this.order.durationUnit] || this.order.durationUnit || "";
      return `${this.order.duration}${unit}`;
    },
    paymentText() {
      if (!this.order) return "—";
      return PAYMENT_TEXT[this.order.paymentStatus] || "未支付";
    },
    payTagType() {
      const s = this.order ? this.order.paymentStatus : null;
      if (s == 1) return "success";
      if (s == 4) return "danger";
      if (s == 2 || s == 3) return "info";
      return "warning";
    },
    refundText() {
      if (!this.order) return "—";
      return REFUND_TEXT[this.order.refundStatus] || "无退款";
    },
    syncText() {
      if (!this.order) return "—";
      return SYNC_TEXT[this.order.ncnpSyncStatus] || "未同步";
    },
  },
  created() {
    this.loadDetail();
  },
  methods: {
    async loadDetail() {
      const id = this.$route.query.id;
      if (!id) {
        this.$message.error("缺少订单参数");
        return;
      }
      this.loading = true;
      try {
        const res = await orderDetail({ id });
        // 兼容 axios 拦截器解包 R 与未解包两种形态
        const data =
          res && res.data && res.data.data ? res.data.data : res && res.data ? res.data : null;
        this.order = data && data.id ? data : null;
        // 以本地 DB 为权威：详情只读本地，不再自动查远端 NCNP。
        // 支付状态由 NCNP 回调 / 对账 Job / 进"去支付"页时的查询回写本地。
      } catch (e) {
        this.$message.error("加载订单详情失败");
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.back();
    },
    goPay() {
      const query = {};
      if (this.order.ncnpOrderId) query.order_id = this.order.ncnpOrderId;
      if (this.order.channelOrderId) query.channel_order_id = this.order.channelOrderId;
      this.$router.push({ path: "/orders/pay", query });
    },
    async copyText(text) {
      if (!text) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        this.$message.success("已复制");
      } catch (e) {
        this.$message.error("复制失败");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.order-detail-page {
  min-height: 60vh;
  background: #f6f8fb;
  padding: 32px 0;
}

.container {
  max-width: 960px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(22, 119, 255, 0.06);
  padding: 32px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  .title {
    margin: 0;
    font-size: 22px;
    color: #1d2129;
  }
}

.desc {
  margin-bottom: 24px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
